import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Anchor, Box, Container, SimpleGrid, Text } from '@mantine/core';
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from '@/lib/contacts';
import { ROUTES } from '@/routes/paths';
import classes from './footer.module.css';

/** Ссылка подвала: страница портала (`to`), документ в новой вкладке (`file`) или адрес связи (`href`). */
type FooterLink =
  | { readonly to: string; readonly key: string }
  | { readonly file: string; readonly key: string }
  | { readonly href: string; readonly text: string };

interface FooterColumn {
  readonly id: string;
  readonly links: readonly FooterLink[];
}

// Пять колонок подвала. Состав и порядок прежние, меняется только оформление.
const COLUMNS: readonly FooterColumn[] = [
  { id: 'company', links: [{ to: ROUTES.ABOUT, key: 'nav.about' }] },
  {
    id: 'contracts',
    links: [
      { to: ROUTES.TERMS, key: 'static.terms.title' },
      { to: ROUTES.SUPPLY, key: 'static.supply.title' },
      { to: ROUTES.TEO, key: 'static.teo.title' },
    ],
  },
  {
    id: 'policy',
    links: [
      { to: ROUTES.POLICY, key: 'footer.policy' },
      { file: '/docs/diadoc-setup.pdf', key: 'footer.diadoc' },
    ],
  },
  {
    id: 'guides',
    links: [
      { file: '/docs/seller-guide.pdf', key: 'footer.sellerGuide' },
      { file: '/docs/buyer-guide.pdf', key: 'footer.buyerGuide' },
    ],
  },
  {
    id: 'contacts',
    links: [
      { href: `mailto:${CONTACT_EMAIL}`, text: CONTACT_EMAIL },
      { href: CONTACT_PHONE_HREF, text: CONTACT_PHONE },
    ],
  },
];

// Единый подвал сайта: до входа и в кабинете состав и разметка одинаковые (решение пользователя 2026-11-02).
export default function AppFooter() {
  const { t } = useTranslation();

  function renderLink(link: FooterLink) {
    const common = { className: classes.link, size: 'sm', underline: 'hover' } as const;
    if ('to' in link) {
      return (
        <Anchor key={link.to} component={Link} to={link.to} {...common}>
          {t(link.key)}
        </Anchor>
      );
    }
    if ('file' in link) {
      return (
        <Anchor key={link.file} href={link.file} target="_blank" rel="noopener noreferrer" {...common}>
          {t(link.key)}
        </Anchor>
      );
    }
    return (
      <Anchor key={link.href} href={link.href} {...common}>
        {link.text}
      </Anchor>
    );
  }

  return (
    <Box component="footer" className={classes.footer}>
      <Container size="lg" pt="lg" pb={28}>
        <SimpleGrid cols={{ base: 2, md: 5 }} spacing="lg" verticalSpacing="md">
          {COLUMNS.map((column) => (
            <div key={column.id}>{column.links.map(renderLink)}</div>
          ))}
        </SimpleGrid>

        {/* Текст о файлах cookie занимает всю ширину подвала, то есть все пять колонок. */}
        <Text c="gray.7" fz={13} mt={18}>
          {t('footer.cookie')}
        </Text>
      </Container>
    </Box>
  );
}
